import React, { useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../config/api';
import { XMarkIcon, PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

interface EditInstitutionProfileModalProps {
  institution: {
    id: string;
    name: string;
    description: string | null;
    logo_url: string | null;
    cover_image_url: string | null;
  };
  onClose: () => void;
  onSaved: () => void;
}

const EditInstitutionProfileModal: React.FC<EditInstitutionProfileModalProps> = ({
  institution,
  onClose,
  onSaved,
}) => {
  const { isDarkMode } = useTheme();
  const [name, setName] = useState(institution.name);
  const [description, setDescription] = useState(institution.description || '');
  const [saving, setSaving] = useState(false);

  // File upload state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(institution.logo_url);
  const [coverPreview, setCoverPreview] = useState<string | null>(institution.cover_image_url);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeCover, setRemoveCover] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleRemoveLogo = () => {
    setRemoveLogo(true);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleRemoveCover = () => {
    setRemoveCover(true);
    setCoverFile(null);
    setCoverPreview(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Remove logo if flagged
      if (removeLogo) {
        await api.delete(`/api/institutions/${institution.id}/logo`);
      }
      // Remove cover if flagged
      if (removeCover) {
        await api.delete(`/api/institutions/${institution.id}/cover`);
      }

      // Upload logo if changed
      if (logoFile) {
        setUploadingLogo(true);
        const formData = new FormData();
        formData.append('file', logoFile);
        await api.post(`/api/institutions/${institution.id}/logo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploadingLogo(false);
      }

      // Upload cover if changed
      if (coverFile) {
        setUploadingCover(true);
        const formData = new FormData();
        formData.append('file', coverFile);
        await api.post(`/api/institutions/${institution.id}/cover`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploadingCover(false);
      }

      // Update text fields
      await api.put(`/api/institutions/${institution.id}`, {
        name: name.trim(),
        description: description.trim() || null,
      });

      onSaved();
      onClose();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
      setUploadingLogo(false);
      setUploadingCover(false);
    }
  };

  const inputClass = `w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors ${
    isDarkMode
      ? 'bg-slate-900/50 border-slate-600 text-white placeholder-gray-500 focus:border-cyan-500'
      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${
        isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white'
      }`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Edit Institution Profile
            </h3>
            <button onClick={onClose} className={`p-1.5 rounded-lg ${
              isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
            }`}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-5">
            {/* Cover Image Upload */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Cover Image
              </label>
              <div
                onClick={() => coverInputRef.current?.click()}
                className={`relative h-32 rounded-xl overflow-hidden cursor-pointer border-2 border-dashed transition-colors ${
                  isDarkMode
                    ? 'border-slate-600 hover:border-slate-500'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {coverPreview ? (
                  <>
                    <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <div className="text-white text-sm font-medium flex items-center gap-2">
                        <ArrowUpTrayIcon className="h-5 w-5" />
                        Change Cover
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full">
                    <PhotoIcon className={`h-8 w-8 mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Click to upload cover image
                    </span>
                  </div>
                )}
                {uploadingCover && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                  </div>
                )}
              </div>
              {coverPreview && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemoveCover(); }}
                  className="text-sm text-red-400 hover:text-red-300 mt-2"
                >
                  Remove cover image
                </button>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverSelect}
                className="hidden"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Logo
              </label>
              <div className="flex items-center gap-4">
                <div
                  onClick={() => logoInputRef.current?.click()}
                  className={`relative w-20 h-20 rounded-2xl overflow-hidden cursor-pointer border-2 border-dashed flex-shrink-0 transition-colors ${
                    isDarkMode
                      ? 'border-slate-600 hover:border-slate-500'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {logoPreview ? (
                    <>
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <ArrowUpTrayIcon className="h-5 w-5 text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <PhotoIcon className={`h-6 w-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                  )}
                  {uploadingLogo && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    </div>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    className={`text-sm font-medium ${isDarkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-500'}`}
                  >
                    {logoPreview ? 'Change logo' : 'Upload logo'}
                  </button>
                  <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Recommended: 256x256px, PNG or JPG
                  </p>
                  {logoPreview && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="text-xs text-red-400 hover:text-red-300 mt-1"
                    >
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoSelect}
                className="hidden"
              />
            </div>

            {/* Name */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Name
              </label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                isDarkMode ? 'text-gray-400 hover:bg-slate-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="px-5 py-2 rounded-xl text-sm font-medium text-white disabled:opacity-50 transition-all"
              style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInstitutionProfileModal;
